/**
 * Taxonomy presets for the parent config editor.
 *
 * ## Design (2026-07-05)
 *
 * Each category is a single prose block (`guidance`) that describes both
 * what to flag AND what NOT to flag. The "enough is enough" line lives
 * inside the text — no numeric thresholds. The LLM adjudicates by reading
 * the parent's actual policy in natural language and applying it directly.
 *
 * The presets below are STARTING POINTS. Every category, including which
 * ones exist at all, is editable in the PWA's Config route. Parents should
 * expect to tune "flag / do not flag" clauses until the model's behavior
 * matches their household's actual values.
 *
 * The `standard` preset covers the common ground; `strict` widens it
 * (drugs, eating disorders, more aggressive tone on ambiguous cases);
 * `test-only` includes a benign, easy-to-trigger category so you can prove
 * the loop is alive without waiting on a real harm event.
 */

import type { HarmsTaxonomy } from '../data/config';

/**
 * Sexual content. The "not explicit sex ed" line is what keeps this
 * category from firing on health-class homework.
 */
const sexualContent = {
  id: 'sexual-content',
  label: 'Sexual content',
  guidance:
    'Flag any image, video, or text depicting sexual acts, nudity intended to arouse, or explicit sexual conversation between users. ' +
    'Do NOT flag: medical or anatomy educational content; classical art nudes in an educational context; PG-13 romantic content (kissing, non-sexual affection); dating-app profile browsing that stays SFW.',
} as const;

/**
 * Predatory contact. The definition here is behavioral (grooming pattern),
 * not demographic — kids talking to peers is normal, adults escalating a
 * relationship with a minor is not.
 */
const predatoryContact = {
  id: 'predatory-contact',
  label: 'Predatory contact',
  guidance:
    'Flag conversations where an adult (or someone claiming to be) is grooming, sexualizing, or trying to move a young person to a private channel — asking for personal info, pushing secrecy, escalating from platonic to sexual, offering gifts or money for images or meetings. ' +
    'Do NOT flag: normal friend chats among peers; parent / relative conversations; teacher, coach, or tutor professional communication; customer-support chats.',
} as const;

/**
 * Bullying / harassment. Covers both directions — kid receiving AND kid
 * dishing out. "Playful ribbing among friends" clause exists so the model
 * doesn't fire on every group DM.
 */
const bullyingHarassment = {
  id: 'bullying-harassment',
  label: 'Bullying or harassment',
  guidance:
    'Flag content where someone is being demeaned, threatened, or coordinated-against — pile-on messages, slurs directed at a person, doxxing, "go kill yourself" style messaging, exclusion campaigns. Flag it whether the child is the target or the source. ' +
    'Do NOT flag: playful ribbing among friends where the tone is clearly affectionate; competitive game trash talk that stays about the game; strong opinions about public figures or politics.',
} as const;

/**
 * Self-harm ideation. Deliberately narrow — we want to catch active
 * intent, not filter out every mention of the topic. Peer support and
 * school essays are usually protective, not concerning.
 */
const selfHarm = {
  id: 'self-harm',
  label: 'Self-harm ideation',
  guidance:
    'Flag content the child is producing or actively seeking that describes methods of self-harm, expresses suicidal intent, or normalizes self-injury as coping. ' +
    'Do NOT flag: educational material such as school essays on mental health; song lyrics unless explicit intent is stated in the surrounding conversation; support-community discussion where the child is offering help to a peer.',
} as const;

/**
 * Substance seeking. Buying/selling/arranging is the concerning
 * behavior; PSAs and harm-reduction info are protective.
 */
const substanceSeeking = {
  id: 'substance-seeking',
  label: 'Substance seeking',
  guidance:
    'Flag messages where the child is trying to buy, sell, or arrange delivery of drugs, alcohol, or nicotine — dealer conversations, dosage questions to strangers, "plug" slang, image-based orders. ' +
    'Do NOT flag: general drug education; harm-reduction material; anti-drug PSAs; parent-approved medical conversations; song lyrics or media that mention drugs without context of use.',
} as const;

/**
 * Violence. High-quality media portrays violence all the time; the flag
 * threshold is "credible real-world violent intent or gratuitous graphic
 * depiction."
 */
const violence = {
  id: 'violence',
  label: 'Violence',
  guidance:
    'Flag content that: (a) expresses credible intent to harm a specific real person, (b) provides instructions for making weapons, or (c) shows gratuitous graphic real-world violence (execution videos, torture, mass-casualty footage). ' +
    'Do NOT flag: fictional violence in games, movies, or shows even when intense; historical / news coverage of conflicts; sports contact; hunting or self-defense discussion; horror fiction.',
} as const;

/**
 * Gambling. Includes loot-box-style predatory monetization, which is
 * more common on a kid's screen than a real casino.
 */
const gambling = {
  id: 'gambling',
  label: 'Gambling',
  guidance:
    'Flag online casinos, sports betting sites, poker for real money, roulette apps, or predatory loot-box mechanics that spend real money for randomized in-game rewards. ' +
    'Do NOT flag: friendly games with no money at stake; educational content about probability or the math of gambling; regulated skill-based games (chess, esports) where prize pools exist but the child is participating skill-based.',
} as const;

/**
 * Test-only. Deliberately trivial and benign — set this to prove the loop
 * is alive with real captures + real LLM inference, without waiting on
 * anything concerning to appear.
 */
const testKittens = {
  id: 'test-kittens',
  label: 'Test — kittens',
  guidance:
    'Flag any screen that clearly shows a domestic cat or kitten (photo, video thumbnail, or drawing). ' +
    'Do NOT flag: text that mentions cats without an image; big cats (lions, tigers); dogs; cartoon-style illustrations that could be any animal. ' +
    'This category exists for smoke-testing that the alert pipeline is alive. Remove it before daily use.',
} as const;

/** Common ground for most families. */
export const standard: HarmsTaxonomy = {
  version: 1,
  categories: [
    sexualContent,
    predatoryContact,
    bullyingHarassment,
    selfHarm,
    substanceSeeking,
    violence,
    gambling,
  ],
};

/** Widens `standard` with a couple of tighter cuts. */
export const strict: HarmsTaxonomy = {
  version: 1,
  categories: [
    ...standard.categories,
    {
      id: 'eating-disorders',
      label: 'Eating disorders',
      guidance:
        'Flag pro-ana, pro-mia, "thinspo" content, or messages encouraging extreme dieting, purging, or restriction as a lifestyle. ' +
        'Do NOT flag: mainstream fitness / nutrition content; recipe browsing; discussion of eating disorders in a recovery or educational context; body-positive content.',
    },
    {
      id: 'radicalization',
      label: 'Extremist content',
      guidance:
        'Flag content actively recruiting into extremist movements, dehumanizing identity groups, or celebrating political / racial violence. ' +
        'Do NOT flag: mainstream political opinion of any stripe; news coverage of extremist events; academic study of radicalization; strong opinions about wars or public figures.',
    },
  ],
};

/**
 * `test-only`: nothing but the smoke-test category. Set this to prove the
 * capture → LLM → alert loop is working end-to-end without waiting on a
 * real harm event.
 */
export const testOnly: HarmsTaxonomy = {
  version: 1,
  categories: [testKittens],
};

export const PRESETS = {
  standard,
  strict,
  'test-only': testOnly,
} as const;
export type PresetName = keyof typeof PRESETS;
